import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Admin-token helpers
//
// SIKKERHED: Vi bruger sessionStorage frem for localStorage. Det betyder at
// tokenet kun holder en browser-tab/-session og ryddes ved tab-luk — mindsker
// XSS-eksponering markant. En fuld httpOnly-cookie-løsning kræver server-side
// session-håndtering, hvilket er overkill til en single-admin token.
export const ADMIN_TOKEN_KEY = "loftebarometer_admin_token";
const _tokenStore = typeof window !== "undefined" ? window.sessionStorage : null;
export const getAdminToken = () => {
    if (!_tokenStore) return "";
    // Migrér eventuelt gammelt token fra localStorage (bagudkompatibilitet)
    const legacy = window.localStorage?.getItem(ADMIN_TOKEN_KEY);
    if (legacy && !_tokenStore.getItem(ADMIN_TOKEN_KEY)) {
        _tokenStore.setItem(ADMIN_TOKEN_KEY, legacy);
        window.localStorage.removeItem(ADMIN_TOKEN_KEY);
    }
    return _tokenStore.getItem(ADMIN_TOKEN_KEY) || "";
};
export const setAdminToken = (t) => {
    if (!_tokenStore) return;
    if (t) _tokenStore.setItem(ADMIN_TOKEN_KEY, t);
    else _tokenStore.removeItem(ADMIN_TOKEN_KEY);
    // Sørg også for at fjerne evt. legacy localStorage-token
    window.localStorage?.removeItem(ADMIN_TOKEN_KEY);
};

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

// Request-interceptor: tilføj X-Admin-Token på admin-endpoints
api.interceptors.request.use((config) => {
    const url = (config.url || "").toLowerCase();
    if (url.includes("/admin/")) {
        const t = getAdminToken();
        if (t) {
            config.headers["X-Admin-Token"] = t;
        }
    }
    return config;
});

// Response-interceptor: ryd token ved 401 (så login-prompt dukker op igen)
api.interceptors.response.use(
    (r) => r,
    (err) => {
        const url = (err?.config?.url || "").toLowerCase();
        if (err?.response?.status === 401 && url.includes("/admin/")) {
            setAdminToken(null);
        }
        return Promise.reject(err);
    },
);

// Anonymt voter-token (gemt lokalt)
export const getVoterToken = () => {
    let token = localStorage.getItem("loftebarometer_voter");
    if (!token) {
        token = `v-${Math.random().toString(36).slice(2)}-${Date.now()}`;
        localStorage.setItem("loftebarometer_voter", token);
    }
    return token;
};

export const fetchParties = () => api.get("/parties").then((r) => r.data);
export const fetchParty = (id) => api.get(`/parties/${id}`).then((r) => r.data);
export const fetchPartyPromises = (id) =>
    api.get(`/parties/${id}/promises`).then((r) => r.data);
export const fetchPartyArticles = (id) =>
    api.get(`/parties/${id}/articles`).then((r) => r.data);
export const fetchPartyStats = (id) =>
    api.get(`/parties/${id}/stats`).then((r) => r.data);
export const fetchOverview = () => api.get("/stats/overview").then((r) => r.data);
export const fetchPromises = (params = {}) =>
    api.get("/promises", { params }).then((r) => r.data);
export const fetchArticles = (params = {}) =>
    api.get("/articles", { params }).then((r) => r.data);
export const fetchPolls = () => api.get("/polls").then((r) => r.data);
export const votePoll = (pollId, optionId) =>
    api
        .post(`/polls/${pollId}/vote`, {
            option_id: optionId,
            voter_token: getVoterToken(),
        })
        .then((r) => r.data);
export const hasVoted = (pollId) =>
    api
        .get(`/polls/${pollId}/has-voted`, { params: { voter_token: getVoterToken() } })
        .then((r) => r.data);

// Quiz
export const fetchQuizQuestions = () => api.get("/quiz/questions").then((r) => r.data);
export const submitQuiz = (answers) =>
    api.post("/quiz/submit", { answers }).then((r) => r.data);

// Stats
export const fetchHeatmap = () => api.get("/stats/category-heatmap").then((r) => r.data);
export const fetchVisits = () => api.get("/stats/visits").then((r) => r.data);
export const trackVisit = (visitorId) =>
    api.post("/stats/visit", { visitor_id: visitorId }).then((r) => r.data);

// Articles
export const reportBrokenArticle = (id) =>
    api.post(`/articles/${id}/report-broken`).then((r) => r.data);

// Flip-flop
export const fetchFlipFlop = (partyId) =>
    api.get(`/parties/${partyId}/flip-flop`).then((r) => r.data);

// Politicians
export const fetchPoliticians = (params = {}) =>
    api.get("/politicians", { params }).then((r) => r.data);
export const fetchPolitician = (id) =>
    api.get(`/politicians/${id}`).then((r) => r.data);

// Admin
export const verifyAdminToken = (token) =>
    api
        .get("/admin/verify", { headers: { "X-Admin-Token": token } })
        .then((r) => r.data);
export const fetchAdminLoginLog = () => api.get("/admin/login-log").then((r) => r.data);
export const unlockAdminIp = (ip) =>
    api.post(`/admin/unlock-ip?ip=${encodeURIComponent(ip)}`).then((r) => r.data);
export const deleteArticle = (id) =>
    api.delete(`/admin/articles/${id}`).then((r) => r.data);
export const purgeNonDanish = () =>
    api.post("/admin/purge-non-danish").then((r) => r.data);
export const triggerVerifyLinks = () =>
    api.post("/admin/verify-links").then((r) => r.data);
export const fetchLinkCheckRuns = () =>
    api.get("/admin/link-check-runs").then((r) => r.data);
export const triggerScrape = () => api.post("/admin/scrape-now").then((r) => r.data);
export const fetchScraperRuns = () => api.get("/admin/scraper-runs").then((r) => r.data);
export const triggerFtDkSync = () => api.post("/admin/sync-folketing").then((r) => r.data);
export const fetchFtDkSyncs = () => api.get("/admin/ft-dk-syncs").then((r) => r.data);
export const triggerDeepScrape = (year = 2000) =>
    api.post(`/admin/deep-scrape?earliest_year=${year}`).then((r) => r.data);
export const fetchDeepScrapeRuns = () => api.get("/admin/deep-scrape-runs").then((r) => r.data);
export const triggerRefreshPoliticians = () =>
    api.post("/admin/refresh-politicians").then((r) => r.data);
export const enrichArticles = () =>
    api.post("/admin/enrich-articles").then((r) => r.data);

// Manuel URL-ingest + Wayback
export const ingestUrl = (url, party_id, article_type) =>
    api.post("/admin/articles/ingest-url", { url, party_id, article_type }).then((r) => r.data);
export const triggerWaybackScrape = (params = {}) =>
    api.post("/admin/wayback-scrape", params).then((r) => r.data);
export const fetchWaybackRuns = () =>
    api.get("/admin/wayback-runs").then((r) => r.data);

// Sundhed
export const fetchAdminHealth = () =>
    api.get("/admin/health").then((r) => r.data);

// Folketingsperioder / kampagneløfter
export const fetchElections = () => api.get("/elections").then((r) => r.data);
export const fetchPartyPromisesByPeriod = (partyId) =>
    api.get(`/parties/${partyId}/promises-by-period`).then((r) => r.data);

// Moderationskø
export const fetchPendingArticles = () =>
    api.get("/admin/pending-articles").then((r) => r.data);
export const fetchPendingCount = () =>
    api.get("/admin/pending-count").then((r) => r.data);
export const approveArticle = (id) =>
    api.post(`/admin/articles/${id}/approve`).then((r) => r.data);
export const rejectArticle = (id) =>
    api.post(`/admin/articles/${id}/reject`).then((r) => r.data);
export const bulkApproveArticles = (article_ids) =>
    api.post("/admin/articles/bulk-approve", { article_ids }).then((r) => r.data);
export const bulkRejectArticles = (article_ids) =>
    api.post("/admin/articles/bulk-reject", { article_ids }).then((r) => r.data);

// Historisk seed (skandaler / kendte løftebrud / faktatjek)
export const seedHistoricalArticles = () =>
    api.post("/admin/seed-historical").then((r) => r.data);
